"use client";

import { useRef, useState, type FormEvent } from "react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { readBrowserAttribution } from "@/components/analytics/browser-attribution";
import { HoneypotField } from "@/components/ui/honeypot-field";
import type { FreeTemplateId } from "@/lib/marketing/free-template-catalog";
import { FREE_TEMPLATE_EMAIL_COPY as COPY } from "@/lib/marketing/free-template-email";
import { HONEYPOT_FIELD_NAME } from "@/lib/public-form-honeypot";

type Status = "idle" | "sending" | "sent" | "error";

/**
 * The optional "Email me the editable version" box under a blank template
 * download. Rendered only when the `free_template_email_capture` flag is on
 * (FreeTemplateDownloadsSection); the download itself stays ungated above it.
 */
export function TemplateEmailCapture({
  template,
  source,
}: {
  template: FreeTemplateId;
  /** PostHog `source`, e.g. "free_rams_template". */
  source: string;
}) {
  const posthog = usePostHog();
  const [email, setEmail] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  // One key per form, so a double-click or network retry is one email.
  const submissionKey = useRef<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "sending") return;
    const honeypotValue = String(new FormData(event.currentTarget).get(HONEYPOT_FIELD_NAME) ?? "");
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError(COPY.invalidEmail);
      setStatus("error");
      return;
    }
    submissionKey.current ??= crypto.randomUUID();
    setStatus("sending");
    setError(null);
    try {
      const res = await fetch("/api/demo/template-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          template,
          marketingConsent: marketing,
          submissionKey: submissionKey.current,
          [HONEYPOT_FIELD_NAME]: honeypotValue,
          attribution: readBrowserAttribution(),
        }),
      });
      const data = (await res.json().catch(() => null)) as { success?: boolean } | null;
      if (!res.ok || data?.success !== true) {
        setError(res.status === 429 ? COPY.rateLimited : res.status === 400 ? COPY.invalidEmail : COPY.failed);
        setStatus("error");
        return;
      }
      setStatus("sent");
      posthog?.capture("free_template_email_requested", {
        source,
        document: template,
        marketing_requested: marketing,
      });
    } catch {
      setError(COPY.failed);
      setStatus("error");
    }
  }

  if (status === "sent") {
    return (
      <div
        role="status"
        data-template-email-capture="sent"
        className="rounded-lg border border-border bg-surface-muted p-5 text-sm text-navy"
      >
        {COPY.sent}
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-template-email-capture="form"
      className="rounded-lg border border-border bg-surface p-5"
    >
      <h3 className="font-heading text-lg font-bold text-navy">{COPY.heading}</h3>
      <p className="mt-1 text-sm text-muted">{COPY.description}</p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <label className="sr-only" htmlFor={`template-email-${template}`}>
          {COPY.emailLabel}
        </label>
        <input
          id={`template-email-${template}`}
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.co.uk"
          aria-invalid={status === "error" && error === COPY.invalidEmail}
          aria-describedby={error ? `template-email-error-${template}` : undefined}
          className="min-h-11 flex-1 rounded-lg border border-border bg-surface px-3 text-base text-navy"
        />
        <button
          type="submit"
          disabled={status === "sending"}
          className="min-h-11 rounded-lg bg-accent px-5 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-60"
        >
          {status === "sending" ? COPY.sending : COPY.submit}
        </button>
      </div>
      <HoneypotField id={`template-email-hp-${template}`} />
      <label className="mt-3 flex items-start gap-2 text-sm text-muted">
        <input
          type="checkbox"
          checked={marketing}
          onChange={(e) => setMarketing(e.target.checked)}
          className="mt-0.5 h-4 w-4"
        />
        <span>{COPY.marketingLabel}</span>
      </label>
      {error ? (
        <p id={`template-email-error-${template}`} role="alert" className="mt-3 text-sm font-medium text-danger">
          {error}
        </p>
      ) : null}
      <p className="mt-3 text-xs text-muted">
        {COPY.privacy}{" "}
        <Link href="/legal/privacy" className="underline hover:text-navy">
          Privacy policy
        </Link>
        .
      </p>
    </form>
  );
}
